/**
 * Managed marketing campaign terms.
 *
 * ⚠️ NOT REVIEWED BY COUNSEL. Drafted to cover the obligations this feature
 * actually creates; it is not legal advice and should be reviewed before launch.
 *
 * ⚠️ VERSION MUST MATCH the backend constant:
 *   fex-platform-service/src/constants/legal-versions.constant.ts
 *     → CURRENT_MARKETING_TERMS_VERSION
 * The server refuses a signature that names any other version, so bumping one
 * without the other blocks every campaign request until they agree again.
 *
 * When the wording changes: edit TERMS, bump MARKETING_TERMS_VERSION here and on
 * the server, and deploy the backend with or before the frontend.
 */
export const MARKETING_TERMS_VERSION = "2026-09-21";

export const MARKETING_TERMS_TITLE = "Campaign advertising authorisation";

export const MARKETING_TERMS: { heading: string; body: string }[] = [
  {
    heading: "You authorise advertising in your name",
    body: "You authorise Licensedproducer to create, place, and manage advertising that features your name, photograph, Licensedproducer profile, and licence information, in the states you select, until you pause or end the campaign. You may withdraw this authorisation at any time by pausing the campaign; advertising already placed may take time to stop.",
  },
  {
    heading: "You are the advertiser of record",
    body: "Advertising run in your name is your advertising under state insurance law. You confirm you hold an active licence in every state you select and that you will keep those licences current. You are responsible for complying with your state insurance department's advertising rules, any filing or licence-number display requirements, and your carriers' and upline's advertising-approval requirements.",
  },
  {
    heading: "You approve the creative before it runs",
    body: "No campaign goes live until you approve the exact wording and imagery. You are responsible for what you approve. Claims must be truthful and not misleading under FTC rules and your state's insurance advertising regulations. Do not approve creative that names a carrier or product you are not appointed to sell.",
  },
  {
    heading: "How your budget is spent",
    body: "Campaign funds are charged once at the time you pay, not as a subscription. Funds are held for your campaign and reduced as advertising spend is reported. Unspent funds can be refunded to the original payment method on request. Money already spent on advertising has been paid to the advertising platform and cannot be refunded. Licensedproducer does not guarantee a number of leads, a cost per lead, any placement, or any result.",
  },
  {
    heading: "Leads and consumer consent",
    body: "People who respond to your campaign give express written consent to be contacted before their details reach you, and that consent record is stored with the lead. You remain responsible for your own compliance when you contact them, including the TCPA, state calling rules, calling hours, Do Not Call obligations, and honouring opt-outs. Automatic follow-up texts require your own approved A2P brand and campaign and will not send without one.",
  },
  {
    heading: "Consumer information",
    body: "Leads contain personal information belonging to real people. Use it only to respond to their insurance enquiry. Do not sell it, share it outside your agency, or use it for unrelated marketing.",
  },
  {
    heading: "What Licensedproducer is not",
    body: "Licensedproducer is an independent private directory. It is not an insurer, agency, or producer, and it is not a government body, Department of Insurance, the NIPR, or the NAIC. Appearing in the registry or running a campaign is not an endorsement or a recommendation of you.",
  },
  {
    heading: "Suspension",
    body: "Campaigns may be paused, rejected, or ended, and funds returned, if a licence lapses, a complaint or regulatory matter arises, an advertising platform refuses the creative, or these terms are breached.",
  },
];

/** Stable fingerprint of the wording actually shown, stored with the signature. */
export async function marketingTermsHash(): Promise<string | undefined> {
  const text = MARKETING_TERMS.map((s) => `${s.heading}\n${s.body}`).join("\n\n");
  try {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
    return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return undefined; // non-secure context: signature still records version + name
  }
}
